<script lang="ts">
  // Dashboard view per INTERFACE-SPECIFICATION.md §3.
  //
  // Layout:
  //   - Quick Actions panel scaffold (role-filtered)
  //   - Four product cards in a 2 × 2 grid
  //   - Recent Activity feed
  //
  // Bootstraps the manifest and the four product catalogues on mount;
  // updates reactively as $effect runs once. The store caches by
  // jurisdiction-product so repeated visits within a session re-render
  // without re-fetching.

  import { onMount } from 'svelte';
  import { bootstrapManifest, fetchCatalogue, catalogueState } from '../lib/catalogue-store.svelte';
  import { PRODUCTS } from '../lib/products';
  import ProductCard from '../components/ProductCard.svelte';
  import QuickActions from '../components/QuickActions.svelte';
  import RecentActivity from '../components/RecentActivity.svelte';

  onMount(async () => {
    await bootstrapManifest();
    await Promise.all(
      PRODUCTS.map((p) => fetchCatalogue(p.jurisdiction, p.product))
    );
  });

  const isBootstrapping = $derived(catalogueState.loading);
</script>

<section class="dashboard">
  <QuickActions />

  <h2 class="section-heading">Catalogue overview</h2>
  {#if isBootstrapping}
    <p class="muted">Loading catalogue…</p>
  {:else if catalogueState.manifestError}
    <div class="banner banner-fail">
      Could not load the manifest: {catalogueState.manifestError}.
      The read-path Worker may be unreachable; retry in a moment.
    </div>
  {:else}
    <div class="dashboard__grid">
      {#each PRODUCTS as product (product.jurisdiction + product.product)}
        <ProductCard {product} />
      {/each}
    </div>
  {/if}

  <RecentActivity />
</section>

<style>
  .dashboard { display: flex; flex-direction: column; gap: var(--space-5); }
  .dashboard__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
    gap: var(--space-4);
  }
  /* Phone-width: minmax(420 px, 1fr) already collapses to one
   * column below 420 px; lift the floor to one column at 768 px so
   * tablets get the broader layout per §12.7 mobile-first surface
   * rules and so ProductCard's metric grids do not crowd at the
   * narrow end of the auto-fit range. */
  @media (max-width: 768px) {
    .dashboard__grid { grid-template-columns: 1fr; }
  }
</style>
